import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Spotlight's top-bar search. Pressing Enter opens the Search page
 * (/membership/:token/search?q=…) for what's in the box; the page itself
 * (SpotlightSearchResults) does the searching. Typing alone changes nothing —
 * results appear when the member asks for them.
 *
 * The first search pushes the Search page onto history and later ones replace
 * it, so Back returns to wherever the member was. × clears the box and goes
 * back there too.
 */
const RETURN_KEY = "spotlightSearchReturn";

export default function SpotlightSearch({ token, primary = "#0274F7" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const searchPath = `/membership/${token}/search`;
  const onSearchPage = location.pathname === searchPath;
  const urlQ = onSearchPage ? new URLSearchParams(location.search).get("q") || "" : "";

  const [q, setQ] = useState(urlQ);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  // Follow the URL: leaving the Search page empties the box; arriving on it (a
  // link, Back/Forward, a search just run) shows its query.
  useEffect(() => {
    setQ(urlQ);
  }, [urlQ, onSearchPage]);

  // "/" focuses the box, the way search works on most member sites.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const open = (value) => {
    const term = value.trim();
    if (!term) return;
    if (!onSearchPage) {
      try {
        sessionStorage.setItem(RETURN_KEY, `${location.pathname}${location.search}`);
      } catch {
        /* private mode — × falls back to Back */
      }
    }
    navigate(`${searchPath}?${new URLSearchParams({ q: term }).toString()}`, { replace: onSearchPage });
  };

  const clear = () => {
    setQ("");
    if (!onSearchPage) {
      inputRef.current?.focus();
      return;
    }
    let back = null;
    try {
      back = sessionStorage.getItem(RETURN_KEY);
    } catch {
      back = null;
    }
    if (back && back !== searchPath) navigate(back, { replace: true });
    else navigate(-1);
  };

  return (
    <div className="relative w-full max-w-md">
      <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-gray-400">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
        </svg>
      </span>
      <input
        ref={inputRef}
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            open(q);
          } else if (e.key === "Escape") {
            clear();
          }
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder="Search courses, communities, events, members… (Enter)"
        aria-label="Search this membership"
        // Focus is styled here in the brand colour, not with focus: utilities —
        // @tailwindcss/forms paints its own blue border and ring on :focus.
        className="h-9 w-full rounded-lg border bg-gray-50 ps-9 pe-9 text-[13.5px] text-gray-800 outline-none transition placeholder:text-gray-400 focus:bg-surface"
        style={{
          borderColor: focused || onSearchPage ? primary : "rgb(var(--c-gray-200))",
          boxShadow: focused ? `0 0 0 3px ${primary}33` : "none",
        }}
      />
      {q ? (
        <button
          type="button"
          onClick={clear}
          aria-label="Clear search"
          className="absolute inset-y-0 end-1.5 my-auto grid h-6 w-6 place-items-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
        </button>
      ) : (
        <kbd className="pointer-events-none absolute inset-y-0 end-2 my-auto hidden h-5 items-center rounded border border-gray-200 px-1.5 text-[10.5px] font-semibold text-gray-400 sm:inline-flex">/</kbd>
      )}
    </div>
  );
}
